import * as React from "react";
import { omitBy } from "lodash-es";
import { casesActions } from "configs";
import {
  FormWatcherCallback,
  PartialNonNullable,
  resolveNumber,
  useCurrentCase,
  useDispatch,
  useParams
} from "duck";
import { useUpdateDentalChartingMutation } from "requests";
import { FormValues, NumberFields } from "./types";

const fieldsToResolve: NumberFields = [
  "periodontalPocketMBuc",
  "periodontalPocketBuccal",
  "periodontalPocketDBuc",
  "periodontalPocketMLingPal",
  "periodontalPocketLingPal",
  "periodontalPocketDLingPal",
  "gingivalRecessionMBuc",
  "gingivalRecessionBuccal",
  "gingivalRecessionDBuc",
  "gingivalRecessionMLingPal",
  "gingivalRecessionLingPal",
  "gingivalRecessionDLingPal"
];

const resolveFields = (values: PartialNonNullable<FormValues>) => {
  return fieldsToResolve.reduce((acc, field) => {
    acc[field] = resolveNumber(values[field], true, true);
    return acc;
  }, {} as Record<keyof FormValues, number | null | undefined>);
};

export const useUpdateDiagnosis = (
  chartId: number | null,
  chartFakeId: string | null
) => {
  const dispatch = useDispatch();
  const { id: caseId } = useParams();

  const [update] = useUpdateDentalChartingMutation({ ignoreResults: true });

  const shouldSync = useCurrentCase(data => data.shouldSync);

  return React.useCallback<FormWatcherCallback<FormValues>>(
    values => {
      const resolvedData = resolveFields(values);
      const data = omitBy(resolvedData, value => value === undefined);
      const variables = { ...values, ...data };

      if (chartId || chartFakeId) {
        dispatch(
          casesActions.updateDentalChart({
            _id: caseId,
            data: {
              ids: [chartId || chartFakeId],
              ...variables
            } as any
          })
        );
      }

      if (chartId && shouldSync) {
        return update({
          variables: { input: { _id: chartId, ...variables } }
        });
      }
    },
    [update, chartId, chartFakeId, shouldSync]
  );
};
